<template>
  <div
    v-if="visible"
    v-show="visible"
    ref="root"
    :style="computedStyle"
    class="vfm"
    :class="[attach === false ? 'vfm--fixed' : 'vfm--absolute']"
  >
    <transition
      v-bind="computedTransition"
      @before-enter="beforeModalEnter"
      @after-enter="afterModalEnter"
      @before-leave="beforeModalLeave"
      @after-leave="afterModalLeave"
    >
      <div
        v-show="visibility.modal"
        ref="vfmContainer"
        class="vfm__container vfm--absolute vfm--outline-none"
        :aria-expanded="visibility.modal.toString()"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        <div
          ref="vfmContent"
          class="vfm__content"
        >
          <slot
            :params="params"
            :close="() => $emit('update:modelValue', false)"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/* eslint-disable vue/no-mutating-props */
import {
  ref,
  reactive,
  onMounted,
  onBeforeUnmount,
  computed,
  nextTick,
  watch,
} from 'vue';

const noop = () => {};

/**
 * Ko dùng
 */
const TransitionState = {
  Enter: 'enter',
  Entering: 'entering',
  Leave: 'leave',
  Leaving: 'leavng',
};


/**
 * Vị trí xuất hiện so với target element
 */
export const MsPopoverPosition = {
  /**
   * Bottom của popover gắn vào Top của target element (chỉ dùng cho trục y)
   */
  Top: 'top',

  /**
   * Top của popover gắn vào Bottom của target element (chỉ dùng cho trục y)
   */
  Bottom: 'bottom',

  /**
   * Left của popover gắn vào Left của target element (chỉ dùng cho trục x)
   */
  Left: 'left',

  /**
   * Right của popover gắn vào Right của target element (chỉ dùng cho trục x)
   */
  Right: 'right',

  /**
   * Popover nằm ở giữa so với target element tùy theo trục đặt biến này (dùng cho cả trục x và y)
   */
  Center: 'center',
};


export default {
  props: {
    // Giá trị dùng để ẩn hiện
    modelValue: { type: Boolean, default: false },
    /**
     * Gắn popover vào component nhất định
     * Hiện tại ko dùng
     */
    attach: {
      type: null,
      default: false,
      validator(val) {
        const type = typeof val;

        if (type === 'boolean' || type === 'string') return true;

        return val.nodeType === Node.ELEMENT_NODE;
      },
    },
    /**
     * Target element cần truyền vào để xác định vị trí xuất hiện popover trên DOM.
     * Hiện tại ko cần dùng đến
     */
    target: {
      type: null,
      default: false,
      validator(val) {
        const type = typeof val;

        if (type === 'boolean' || type === 'string') return true;

        return val.nodeType === Node.ELEMENT_NODE;
      },
    },
    /**
     * Vị trí xuất hiện.
     * x: vị trí đặt popover vào trục x của target element left, center, right
     * y: vị trí đặt popover vào trục y của target element top, center, bottom
     * offsetX: margin-left của popover so với trục x
     * offsetY: margin-top của popover so với trục y
     * isOuterX: nếu true tâm của popover sẽ hướng ra ngoài target element theo trục X ngược lại hướng vào trong
     * isOuterY: nếu true tâm của popover sẽ hướng ra ngoài target element theo trục Y ngược lại hướng vào trong
     */
    position: {
      type: Object,
      default: () => ({
        x: MsPopoverPosition.Left,
        y: MsPopoverPosition.Bottom,
        offsetX: 0,
        offsetY: 0,
        isOuterX: false,
        isOuterY: true,
      }),
      validator(val) {
        if (
          // offsetX chỉ cho phép số
          typeof val.offsetX !== 'number' ||
          // offsetY chỉ cho phép số
          typeof val.offsetY !== 'number' || 
          // Trục X chỉ nhận giá trị left, center, right
          (
            val.x !== MsPopoverPosition.Left && 
            val.x !== MsPopoverPosition.Right &&
            val.x !== MsPopoverPosition.Center
          ) ||
          // Trục Y chỉ nhận giá trị top, center, bottom
          (
            val.y !== MsPopoverPosition.Top &&
            val.y !== MsPopoverPosition.Bottom &&
            val.y !== MsPopoverPosition.Center
          ) ||
          // isOuterX chỉ nhận kiểu boolean
          typeof val.isOuterX !== 'boolean' ||
          // isOuterY chỉ nhận kiểu boolean
          typeof val.isOuterY !== 'boolean'
        ) {
          return false;
        }
        return true;
      },
    },

    // Hiện tại ko dùng đến
    transition: { type: [String, Object], default: 'vfm' },

    // Có tự đống đánh z-index hay ko
    zIndexAuto: { type: Boolean, default: true },

    // z-index cơ sở popover sẽ có z-index từ số cơ sở này trở lên
    zIndexBase: { type: [String, Number], default: 1000 },

    // Truyền vào z-index tĩnh nếu ko dùng tự động đánh z-index
    zIndex: { type: [Boolean, String, Number], default: false },

    // Chiều rộng
    width: {
      type: Number,
      default: 0,
    },

    // Chiều cao
    height: {
      type: Number,
      default: 0,
    },
  },
  emits: [
    'update:modelValue',
    'click-outside',
    'before-open',
    'opened',
    'before-close',
    'closed',
    '_before-open',
    '_opened',
    '_closed',
  ],
  setup(props, { emit }) {
    const uid = Symbol('vfm');
    const root = ref(null);
    const vfmContainer = ref(null);
    const vfmContent = ref(null);

    /**
     * Vị trí index của popover trong danh sách popover
     */
    const modalStackIndex = ref(null);

    /**
     * Ẩn hiện container
     */
    const visible = ref(false);

    /**
     * Ẩn hiện popover
     */
    const visibility = reactive({
      modal: false,
    });

    /**
     * Trạng thái hiện tại của transition
     */
    const modalTransitionState = ref(null);

    /**
     * Ko biết
     */
    const _stopEvent = ref(false);

    /**
     * Param truyền vào từ component cha
     */
    const params = ref({});

    let resolveToggle = noop;

    let rejectToggle = noop;

    /**
     * Biến để bind lên transition component
     */
    const computedTransition = computed(() => {
      if (typeof props.transition === 'string')
        return { name: props.transition };
      return { ...props.transition };
    });

    /**
     * Tính style của container để đặt đúng vị trí theo target element và position
     * @author: BMThang(02/11/2022)
     */
    const computedStyle = computed(() => {
      const { position, bindStyle } = props;
      // Lấy target element
      const target = getTargetElement();

      // Nếu có target element và ref của content thì mới thực hiện
      if (target && vfmContent.value) {
        try {
          let { top, left, width, height } = target.getBoundingClientRect();
          let { width: popoverWidth, height: popoverHeight } = vfmContent.value.getBoundingClientRect();

          // Nếu truyền từ props width và height thì lấy width và hay của popover theo cái này
          if (props.width && props.height) {
            popoverWidth = props.width;
            popoverHeight = props.height;
          }

          if (position.x === MsPopoverPosition.Left && position.isOuterX) {
            left -= popoverWidth;
          }

          if (position.x === MsPopoverPosition.Right) {
            if (position.isOuterX) {
              left += width
            } else {
              left += width - popoverWidth;
            }
          }

          if (position.x === MsPopoverPosition.Center) {
            left += parseInt(width / 2) - parseInt(popoverWidth / 2);
          }
          
          if (position.y === MsPopoverPosition.Bottom) {
            if (position.isOuterY) {
              top += height;
            } else {
              top += height - popoverHeight;
            }
          }

          if (position.y === MsPopoverPosition.Top) {
            if (position.isOuterY) {
              top -= popoverHeight;
            }
          }

          if (position.y === MsPopoverPosition.Center) {
            top += parseInt(height / 2) - parseInt(popoverHeight / 2);
          }

          top += position.offsetY && !isNaN(position.offsetY) ? position.offsetY : 0;
          left += position.offsetX && !isNaN(position.offsetX) ? position.offsetX : 0;

          return {
            top: `${top}px`,
            left: `${left}px`,
            ...bindStyle,
          }
        } catch {
          return {
            ...bindStyle,
            top: 0,
            left: 0,
          }
        }
      }
      return {
        ...bindStyle,
        top: 0,
        left: 0,
      }
    });

    /**
     * Xem popover đã sẵn sàng để bị xóa chưa
     */
    const isComponentReadyToBeDestroyed = computed(() => {
      return modalTransitionState.value === TransitionState.Leave;
    });

    /**
     * Tính z-index
     */
    const calculateZIndex = computed(() => {
      if (props.zIndex === false) {
        if (props.zIndexAuto) {
          return +props.zIndexBase + 2 * (modalStackIndex.value || 0);
        } else {
          return false;
        }
      } else {
        return props.zIndex;
      }
    });

    /**
     * Bind style từ z-index
     */
    const bindStyle = computed(() => {
      return {
        ...(calculateZIndex.value !== false && {
          zIndex: calculateZIndex.value,
        }),
      };
    });

    // Xử lý thay đổi ẩn hiện popover
    watch(
      () => props.modelValue,
      (value) => {
        if (_stopEvent.value) {
          _stopEvent.value = false;
          return;
        }
        mounted();
        if (!value) {
          if (emitEvent('before-close', true)) {
            rejectToggle('hide');
            return;
          }
          close();
        }
      }
    );

    // Ko dùng
    watch(() => props.attach, mounted);

    // Nếu đã sẵn sàng bị xóa thì ẩn container
    watch(
      isComponentReadyToBeDestroyed,
      (val) => {
        if (val) {
          visible.value = false;
          vfmContainer.value.style.display = 'none';
        }
      },
      {
        flush: 'post',
      }
    );

    // Lần đầu mout thì đẩy thông tin của popover vào danh sách popover
    props.api.modals.push(getModalInfo());

    onMounted(() => {
      mounted();
    });

    onBeforeUnmount(() => {
      close();
      root?.value?.remove();
      let index = props.api.modals.findIndex((vm) => vm.uid === uid);

      props.api.modals.splice(index, 1);
    });

    /**
     * Trả về thông tin chi tiết của popover
     */
    function getModalInfo() {
      return {
        uid,
        props,
        emit,
        vfmContainer,
        vfmContent,
        getAttachElement,
        modalStackIndex,
        visibility,
        toggle,
        params,
      };
    }
    /**
     * Xử lý ko popover đc mount
     */
    function mounted() {
      if (props.modelValue) {
        emit('_before-open', createModalEvent({ type: '_before-open' }));
        if (emitEvent('before-open', false)) {
          rejectToggle('show');
          return;
        }

        // Event bấm ra ngoài
        window.addEventListener('click', clickOutSideFunc);

        let target = getAttachElement();
        if (target || props.attach === false) {
          if (props.attach !== false) {
            if (root.value) {
              target.appendChild(root.value);
            } else {
              visible.value = true;
              nextTick(() => {
                mounted();
              });
              return;
            }
          }

          let index = props.api.openedModals.findIndex((vm) => vm.uid === uid);

          if (index !== -1) {
            // if this is already exist in modalStack, delete it
            props.api.openedModals.splice(index, 1);
          }
          props.api.openedModals.push(getModalInfo());

          modalStackIndex.value = props.api.openedModals.length - 1;

          props.api.openedModals
            .filter((vm) => vm.uid !== uid)
            .forEach((vm, index) => {
              if (vm.getAttachElement() === target) {
                // if vm and this have the same attach element
                vm.modalStackIndex.value = index;
              }
            });
          visible.value = true;

          // Nếu có truyền width và height từ props thì mới áp dụng transition
          if (props.width && props.height) {
            nextTick(() => {
              startTransitionEnter();
            });
          } else {
            // Ngược lại tắt luôn
            startTransitionEnter();
          }
        } else if (target !== false) {
          console.warn('Unable to locate target '.concat(props.attach));
        }
      }
    }
    /**
     * Xóa popover khỏi danh sách popover và đóng
     */
    function close() {
      let index = props.api.openedModals.findIndex((vm) => vm.uid === uid);
      if (index !== -1) {
        // remove this in modalStack
        props.api.openedModals.splice(index, 1);
      }
      startTransitionLeave();
    }
    /**
     * Lấy element để gắn popover vào
     * Hiện tại ko dùng
     */
    function getAttachElement() {
      let target;
      if (props.attach === false) {
        target = false;
      } else if (typeof props.attach === 'string') {
        // CSS selector
        if (window) {
          target = window.document.querySelector(props.attach);
        } else {
          target = false;
        }
      } else {
        // DOM Element
        target = props.attach;
      }
      return target;
    }
    /**
     * Lấy element target để gắn popover vào vị trí thích hợp
     * @param {*} inputTarget ID hoặc class hoặc element mặc định là target của popover hiện tại
     * @author: BMThang(02/11/2022)
     */
    const getTargetElement = (inputTarget = props.target) => {
      let target;
      if (inputTarget === false) {
        // Nếu ko truyền vào thì trả về false
        target = false;
      } else if (typeof inputTarget === 'string') {
        // Nếu là kiểu string thì dùng CSS selector để tìm element
        if (window) {
          target = window.document.querySelector(inputTarget);
        } else {
          target = false;
        }
      } else {
        // Nếu truyền thẳng element vào thì trả về element luôn
        target = inputTarget;
      }
      return target;
    }
    /**
     * Hiện popover
     */
    function startTransitionEnter() {
      visibility.modal = true;
    }
    /**
     * Ẩn popover
     */
    function startTransitionLeave() {
      visibility.modal = false;
    }
    
    /**
     * Xử lý trước khi popover xuất hiện
     */
    function beforeModalEnter() {
      modalTransitionState.value = TransitionState.Entering;
    }

    /**
     * Xử lý sau khi popover xuất hiện
     */
    function afterModalEnter() {
      modalTransitionState.value = TransitionState.Enter;
      emit('_opened');
      emit('opened', createModalEvent({ type: 'opened' }));
      resolveToggle('show');
    }

    /**
     * Hàm xử lý trước khi đóng popover
     */
    function beforeModalLeave() {
      modalTransitionState.value = TransitionState.Leaving;
    }
    /**
     * Hàm xử lý sau khi đóng popover
     */
    function afterModalLeave() {
      modalTransitionState.value = TransitionState.Leave;
      modalStackIndex.value = null;

      let stopEvent = false;
      const event = createModalEvent({
        type: 'closed',
        stop() {
          stopEvent = true;
        },
      });
      emit('_closed');
      emit('closed', event);
      resolveToggle('hide');
      if (stopEvent) return;

      // Clear param
      params.value = {};
    }
    /**
     * Đóng popover khi click bên ngoài popover và element target
     * @param {object} e event object
     * @author: BMThang(02/11/2022)
     */
    const clickOutSideFunc = (e) => {
      // Lặp qua tất cả các popover đang mở
      // Nếu bấm vào 1 trong các popover này đồng thời popover hiện tại phải chừa target element của popover đó
      // Thì ko cần đóng
      const openedModals = props.api.openedModals;
      if (Array.isArray(openedModals) && vfmContent.value) {
        const openedModalsCount = openedModals.length;
        for (let index = 0; index < openedModalsCount; index++) {
          const openedModal = openedModals[index];

          // Lấy content element của popover
          const modalContent = openedModal.vfmContent?._rawValue;

          // Lấy target element của popover
          const modalTarget = getTargetElement(openedModal.params?._rawValue?.target || false);

          if (modalContent && modalTarget) {
            if (modalContent.contains(e.target) && vfmContent.value.contains(modalTarget)) {
              return;
            }
          }
        }
      }

      // Nếu bấm ra ngoài popover hoặc target element thì đóng
      const target = getTargetElement();
      if (vfmContent.value && target) {
        if (!vfmContent.value.contains(e.target) && !target.contains(e.target)) {
          emit('update:modelValue', false)
        }
      }
    }

    /**
     * Tạo event để xử lỹ các hành động và emit ngoài
     */
    function createModalEvent(eventProps = {}) {
      if (eventProps.type === 'opened') {
        // Khi mở popover thì thêm event tới window để kiểm tra nếu bấm ra ngoài thì tắt popover
        window.addEventListener('click', clickOutSideFunc);
      } else if (eventProps.type === 'closed') {
        // Khi đóng popover thì xóa event bên trên
        window.removeEventListener('click', clickOutSideFunc);
      }
      return {
        ref: getModalInfo(),
        ...eventProps,
      };
    }

    /**
     * Emit event ra ngoài
     */
    function emitEvent(eventType, value) {
      let stopEvent = false;
      const event = createModalEvent({
        type: eventType,
        stop() {
          stopEvent = true;
        },
      });
      emit(eventType, event);
      if (stopEvent) {
        _stopEvent.value = true;
        nextTick(() => {
          emit('update:modelValue', value);
        });
        return true;
      }
      return false;
    }

    /**
     * Ẩn hiện popover
     */
    function toggle(show, _params) {
      return new Promise((resolve, reject) => {
        resolveToggle = (res) => {
          resolve(res);
          resolveToggle = noop;
        };
        rejectToggle = (err) => {
          reject(err);
          rejectToggle = noop;
        };
        const value = typeof show === 'boolean' ? show : !props.modelValue;
        if (value && arguments.length === 2) {
          params.value = _params;
        }
        emit('update:modelValue', value);
      });
    }
    return {
      root,
      vfmContainer,
      vfmContent,
      computedTransition,
      visible,
      visibility,
      params,
      calculateZIndex,
      bindStyle,
      beforeModalEnter,
      afterModalEnter,
      beforeModalLeave,
      afterModalLeave,
      computedStyle,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';
@import '@/assets/scss/transitions.scss';
.vfm--fixed {
  position: fixed;
}
.vfm--absolute {
  position: absolute;
}
.vfm--inset {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.vfm--overlay {
  background-color: rgba(0, 0, 0, 0.5);
}
.vfm--prevent-none {
  pointer-events: none;
}
.vfm--prevent-auto {
  pointer-events: auto;
}
.vfm--outline-none:focus {
  outline: none;
}
.vfm-enter-active,
.vfm-leave-active {
  transition: opacity 0.2s;
}
.vfm-enter-from,
.vfm-leave-to {
  opacity: 0;
}

.vfm--touch-none {
  touch-action: none;
}
.vfm--select-none {
  user-select: none;
}

.vfm--resize-tr,
.vfm--resize-br,
.vfm--resize-bl,
.vfm--resize-tl {
  width: 12px;
  height: 12px;
  z-index: 10;
}

.vfm--resize-t {
  top: -6px;
  left: 0;
  width: 100%;
  height: 12px;
  cursor: ns-resize;
}
.vfm--resize-tr {
  top: -6px;
  right: -6px;
  cursor: nesw-resize;
}
.vfm--resize-r {
  top: 0;
  right: -6px;
  width: 12px;
  height: 100%;
  cursor: ew-resize;
}
.vfm--resize-br {
  bottom: -6px;
  right: -6px;
  cursor: nwse-resize;
}
.vfm--resize-b {
  bottom: -6px;
  left: 0;
  width: 100%;
  height: 12px;
  cursor: ns-resize;
}
.vfm--resize-bl {
  bottom: -6px;
  left: -6px;
  cursor: nesw-resize;
}
.vfm--resize-l {
  top: 0;
  left: -6px;
  width: 12px;
  height: 100%;
  cursor: ew-resize;
}
.vfm--resize-tl {
  top: -6px;
  left: -6px;
  cursor: nwse-resize;
}
</style>
